import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, RefreshCw } from 'lucide-react';
import StaffScanGate from '@/components/StaffScanGate';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { GsPickPanel } from '@/components/tissue/GsPickPanel';
import { JwStatusBadge, fmtDate, sortSummary } from '@/components/tissue/JwStatusBadge';
import { getOrder, listOrders } from '@/services/tissueJwApi';

const TABS = { pending: 'REQUESTED', picking: 'PICKING', done: 'TRANSFERRED,AT_JOBBER,RECEIVING,COMPLETED' };

const TissueJwPicking = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState('pending');
  const [orders, setOrders] = useState([]);
  const [sel, setSel] = useState(null);

  const load = useCallback(() => listOrders({ status: TABS[tab] }).then((r) => setOrders(r.orders || []))
    .catch(() => toast.error('Could not load requests')), [tab]);
  useEffect(() => { load(); }, [load]);
  const open = (id) => getOrder(id).then(setSel).catch(() => toast.error('Could not open request'));
  const changed = (nextTab) => { if (nextTab && nextTab !== tab) setTab(nextTab); else load(); if (sel) open(sel.jw_id); };

  return (
    <StaffScanGate stationName="Greige Storage — Tissue Job Work">
      <div className="min-h-screen bg-slate-50" data-testid="gs-tissue-jw">
        <header className="bg-white border-b">
          <div className="max-w-7xl mx-auto px-4 md:px-6 min-h-16 py-2 flex items-center gap-3">
            <Button variant="ghost" onClick={() => navigate('/sections/greige-storage')} data-testid="gs-tjw-back"><ArrowLeft className="w-5 h-5" /></Button>
            <div className="min-w-0"><h1 className="text-lg sm:text-xl font-bold">Tissue Job Work Requests</h1>
              <p className="text-xs sm:text-sm text-gray-500 hidden sm:block">Accept → pick bundles of the sort → Internal Transfer to Tissue (goes straight to the jobber)</p></div>
            <Button variant="outline" className="ml-auto" onClick={load} data-testid="gs-tjw-refresh"><RefreshCw className="w-4 h-4" /></Button>
          </div>
        </header>
        <main className="max-w-7xl mx-auto px-4 md:px-6 py-6 grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-5">
          <div className="space-y-3 min-w-0">
            <Tabs value={tab} onValueChange={(v) => { setTab(v); setSel(null); }}>
              <TabsList className="w-full">
                <TabsTrigger value="pending" className="flex-1" data-testid="gs-tjw-tab-pending">Pending</TabsTrigger>
                <TabsTrigger value="picking" className="flex-1" data-testid="gs-tjw-tab-picking">Picking</TabsTrigger>
                <TabsTrigger value="done" className="flex-1" data-testid="gs-tjw-tab-done">Transferred</TabsTrigger>
              </TabsList>
            </Tabs>
            {!orders.length && <p className="text-sm text-gray-400 text-center py-8" data-testid="gs-tjw-empty">Nothing here</p>}
            {orders.map((o) => (
              <Card key={o.jw_id} onClick={() => open(o.jw_id)} data-testid={`gs-tjw-card-${o.jw_id}`}
                className={`cursor-pointer transition-colors hover:border-[#2D5F4F] ${sel?.jw_id === o.jw_id ? 'border-[#2D5F4F] ring-1 ring-[#2D5F4F]' : ''}`}>
                <CardContent className="p-3 space-y-1">
                  <div className="flex justify-between gap-2"><span className="font-mono text-sm">{o.jw_id}</span><JwStatusBadge status={o.status} /></div>
                  <p className="text-xs text-gray-600 font-mono">{sortSummary(o)}</p>
                  <p className="text-[11px] text-gray-400">{o.jobber_name} · {fmtDate(o.created_at)}</p>
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="min-w-0">{sel ? <GsPickPanel order={sel} onChanged={changed} /> : <p className="text-sm text-gray-400 pt-10 text-center">Pick a request on the left</p>}</div>
        </main>
      </div>
    </StaffScanGate>
  );
};

export default TissueJwPicking;
